import { BridgeClient } from 'app/service/bridge/BridgeClient';
import { calcWidgetCurve, prepareRecentAnalyticsDateRange } from 'util/appUtils';

/**
 * Creates an analytics API backed by the bridge transport.
 *
 * Bridge actions used:
 *   analytics.top_queries                → BridgeUseCase → AnalyticsService (popular queries)
 *   analytics.zero_results               → BridgeUseCase → AnalyticsService (zero-result queries)
 *   analytics.lost_opportunities         → BridgeUseCase → AnalyticsService (low-CTR queries)
 *   analytics.products_popular_from_search → BridgeUseCase → AnalyticsService (popular products)
 *
 * Bridge response shape (top_queries / zero_results / lost_opportunities):
 *   {
 *     result: 'success',
 *     data: {
 *       records: [{ query, users, total, click, noaction, refine, ctr }],
 *       users:   { total, filteredTotal, otherTotal },
 *       queries: { total, filteredTotal, otherTotal, ctr },
 *       curve:   [{ _id: { year, month, day }, ctr, total, click, noaction, refine }],
 *     }
 *   }
 *
 * Data model note:
 *   The plugin analytics table has NO user identity (no user_id, no session_id).
 *   - Backend `users` per record = search event count, not unique users.
 *   - `refine` is always 0 — not tracked in the plugin model.
 *   - `ctr` is click events / search events per query (event-level ratio).
 *
 * Admin record model (normalized):
 *   { query, searches, click, noaction, ctr }
 *   - `searches` replaces `users` / `total` (honest label for event count)
 *   - `refine` is intentionally dropped — always 0, adds no value
 */

const BRIDGE_ACTIONS = {
  top_queries:                   'analytics.top_queries',
  zero_results:                  'analytics.zero_results',
  lost_opportunities:            'analytics.lost_opportunities',
  products_popular_from_search:  'analytics.products_popular_from_search',
  query_detail:                   'analytics.query_detail',
  report_export:                  'analytics.report_export',
};

export function createBridgeAnalyticsApi() {
  const client = new BridgeClient();

  /**
   * Normalize a popular-product row from handleAnalyticsRead response.
   * Keeps the id and translated name separate. A missing name means the product
   * was deleted or is unavailable in the active language/shop context.
   */
  function normalizePopularProduct(raw) {
    return {
      id:        raw.id_product,
      productId: raw.id_product,
      name:      raw.name || null,
      count:     raw.count ?? 0,
    };
  }

  /**
   * Normalize a single bridge record to the admin model.
   * Drops `refine`, renames `users` → `searches`.
   */
  function normalizeRecord(raw) {
    return {
      query:    raw.query,
      language_id: raw.language_id == null ? null : String(raw.language_id),
      searches: raw.users ?? raw.total ?? 0,
      click:    raw.click    ?? 0,
      noaction: raw.noaction ?? 0,
      ctr:      raw.ctr      ?? 0,
    };
  }

  /**
   * Normalize the raw curve array to the legacy widget-compatible shape.
   * Uses calcWidgetCurve (same logic as legacy Analytic.js sagas).
   *
   * Curve shapes by report type:
   *   top_queries        → [{ total, ctr, label }]
   *   zero_results       → [{ pv, label }]       ← pv = page views alias for chart
   *   lost_opportunities → [{ total, label }]
   */
  function normalizeCurve(curveArray, reportType) {
    if (!Array.isArray(curveArray) || curveArray.length === 0) {
      return { data: [] };
    }

    const calculated = calcWidgetCurve(curveArray);
    const keys = Object.keys(calculated);

    if (reportType === 'top_queries') {
      return {
        data: keys.map((k) => ({
          total: calculated[k].total,
          click: calculated[k].click ?? 0,
          ctr:   parseFloat((calculated[k].ctr * 100).toFixed(2)),
          label: calculated[k].label,
        })),
      };
    }

    if (reportType === 'zero_results') {
      return {
        data: keys.map((k) => ({
          pv:    calculated[k].total,
          label: calculated[k].label,
        })),
      };
    }

    // lost_opportunities
    return {
      data: keys.map((k) => ({
        total: calculated[k].total,
        label: calculated[k].label,
      })),
    };
  }

  return {
    async exportReport(_shopId, reportType, {
      from = null,
      to = null,
      timeframeDays = null,
    } = {}) {
      if (!BRIDGE_ACTIONS[reportType] || reportType === 'products_popular_from_search') {
        throw new Error(`Unknown analytics reportType: "${reportType}"`);
      }
      const range = (!from && !to && timeframeDays)
        ? prepareRecentAnalyticsDateRange(timeframeDays)
        : { from, to };
      const payload = { report_type: reportType };
      if (range.from) payload.from = range.from;
      if (range.to) payload.to = range.to;

      const response = await client.sendMessageWithResponse(BRIDGE_ACTIONS.report_export, payload);
      const data = response.data || {};
      return {
        reportType: data.report_type || reportType,
        fileName: data.file_name || `kea-${reportType.replace(/_/g, '-')}.csv`,
        mimeType: data.mime_type || 'text/csv;charset=utf-8',
        content: typeof data.content === 'string' ? data.content : '',
        rowCount: Number(data.row_count || 0),
      };
    },

    async loadQueryDetail(_shopId, query, {
      from = null,
      to = null,
      timeframeDays = null,
    } = {}) {
      const normalizedQuery = String(query || '').trim();
      if (!normalizedQuery) throw new Error('query is required');
      const range = (!from && !to && timeframeDays)
        ? prepareRecentAnalyticsDateRange(timeframeDays)
        : { from, to };
      const payload = { query: normalizedQuery };
      if (range.from) payload.from = range.from;
      if (range.to) payload.to = range.to;
      const response = await client.sendMessageWithResponse(BRIDGE_ACTIONS.query_detail, payload);
      const detail = response.data || {};
      return {
        query: detail.query || normalizedQuery,
        metrics: {
          searches: Number(detail.metrics?.searches || 0),
          clicks: Number(detail.metrics?.clicks || 0),
          noActions: Number(detail.metrics?.no_actions || 0),
          ctr: Number(detail.metrics?.ctr || 0),
        },
        curve: (detail.curve || []).map((row) => ({
          date: row.date,
          searches: Number(row.searches || 0),
          clicks: Number(row.clicks || 0),
          noActions: Number(row.no_actions || 0),
          ctr: Number(row.ctr || 0),
        })),
        relatedQueries: [],
        relatedProducts: [],
        relatedQueriesUnavailable: false,
        relatedProductsUnavailable: false,
        relatedQueriesSampled: false,
        relatedQueriesSampleSize: 0,
      };
    },

    /**
     * Load a single analytics report from the plugin bridge.
     *
     * @param {string} _shopId  — not used by bridge (plugin context comes from plugin side)
     * @param {string} reportType — 'top_queries' | 'zero_results' | 'lost_opportunities' | 'products_popular_from_search'
     * @param {{ limit?: number, from?: string|null, to?: string|null, timeframeDays?: number|null }} params
     * @returns {Promise<{ data, users, queries, curve }>}
     */
    async loadReport(_shopId, reportType, {
      limit = 20,
      from = null,
      to = null,
      timeframeDays = null,
    } = {}) {
      const action = BRIDGE_ACTIONS[reportType];
      if (!action) {
        throw new Error(`Unknown analytics reportType: "${reportType}"`);
      }

      const payload = { limit };
      const range = (!from && !to && timeframeDays)
        ? prepareRecentAnalyticsDateRange(timeframeDays)
        : { from, to };
      if (range.from) payload.from = range.from;
      if (range.to)   payload.to   = range.to;

      // products_popular_from_search uses handleAnalyticsRead which returns
      // { result, data: [...], meta } — a flat array, not the records/users/queries envelope.
      if (reportType === 'products_popular_from_search') {
        const response = await client.sendMessageWithResponse(action, payload);
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          data:    rows.map(normalizePopularProduct),
          users:   null,
          queries: null,
          curve:   null,
        };
      }

      const response = await client.sendMessageWithResponse(action, payload);
      const data = response.data || {};

      return {
        data:    (data.records || []).map(normalizeRecord),
        users:   data.users   || { total: 0, filteredTotal: 0, otherTotal: 0 },
        queries: data.queries || { total: 0, filteredTotal: 0, otherTotal: 0, ctr: 0 },
        curve:   normalizeCurve(data.curve || [], reportType),
      };
    },
  };
}
